import React, { useEffect, useState } from 'react';
import Toastify, { notifyError, notifySuccess } from "../../../utils/tostfy";
import MasterSuccessModal from '../MasterSuccessModal';
import { handleApiCall } from '../../../apis';
import { endpoint } from '../../../utils/endpoints';
import EditIcon from '../../../assets/image/edit-icon.svg'
import LeftAngle from '../../../assets/image/left-angle.svg'
import RightAngle from '../../../assets/image/right-angle.svg'
import PermissionGate from '../../../Components/PermissionGate/PermissionGate';
import { MODULES } from '../../../utils/permissions';
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../../utils/search";
import PageNumberInput from "../../../Components/Pagination/PageNumberInput";

const LocationMaster = () => {
    const [locations, setLocations] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [addLocation, setAddLocation] = useState(false);
    const [editLocation, setEditLocation] = useState(false);
    const [locationName, setLocationName] = useState('');
    const [locationError, setLocationError] = useState('');
    const [savingLocation, setSavingLocation] = useState(false);
    const [editingLocationId, setEditingLocationId] = useState('');
    const [editLocationName, setEditLocationName] = useState('');
    const [editLocationError, setEditLocationError] = useState('');
    const [savingEditLocation, setSavingEditLocation] = useState(false);
    const [statusUpdatingId, setStatusUpdatingId] = useState('');
    const [successModal, setSuccessModal] = useState({ open: false, message: '' });
    const openMasterSuccess = (message) =>
        setSuccessModal({ open: true, message: message || 'Saved successfully' });
    const closeMasterSuccess = () => setSuccessModal({ open: false, message: '' });

    const [itemsPerPage, setItemsPerPage] = useState(10);

    const mapLocationData = (list) =>
        (list || []).map((item) => ({
            id: item?._id || item?.id || '',
            location: item?.name || item?.location_name || '-',
            statusCode: item?.status === 1 ? 1 : 0,
            status: item?.status === 1 ? 'Active' : 'Inactive',
        }));
        const searchForApi = (() => {
            const s = clampSearchInput(debouncedSearch).trim();
            if (!s) return '';
            if (/^\+?[\d\s-]+$/.test(s)) return s.replace(/\D/g, '');
            return s;
        })();
    const fetchLocationList = async () => {
        setListLoading(true);
        try {
            const response = await handleApiCall(
                'get',
                `${endpoint.listOflocation}?search=${encodeURIComponent(searchForApi)}&page=${currentPage}&limit=${itemsPerPage}`,
                {}
            );
            if (response?.data?.code === 200) {
                const d = response?.data?.data || {};
                const raw = d?.location_data || d?.locations || d;
                const mapped = mapLocationData(Array.isArray(raw) ? raw : []);
                setLocations(mapped);
                setTotalRecords(d?.total ?? mapped.length);
                setTotalPages(Math.max(1, d?.total_pages ?? 1));
            } else {
                const errMsg =
                    response?.response?.data?.errorMessage ||
                    response?.response?.data?.message ||
                    response?.data?.message ||
                    'Failed to load locations';
                notifyError(errMsg);
            }
        } catch {
            notifyError('Failed to load locations');
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        fetchLocationList();
    }, [debouncedSearch, currentPage, itemsPerPage]);

    const resetAddLocationForm = () => {
        setLocationName('');
        setLocationError('');
    };

    const openAddLocation = () => {
        resetAddLocationForm();
        setAddLocation(true);
    };

    const closeAddLocation = () => {
        setAddLocation(false);
        resetAddLocationForm();
    };

    const openEditLocation = (loc) => {
        setEditingLocationId(loc?.id || '');
        setEditLocationName(loc?.location || '');
        setEditLocationError('');
        setEditLocation(true);
    };

    const closeEditLocation = () => {
        setEditLocation(false);
        setEditingLocationId('');
        setEditLocationName('');
        setEditLocationError('');
    };

    const handleEditLocationSubmit = async (e) => {
        e?.preventDefault();
        const err = validateLocationName(editLocationName);
        setEditLocationError(err);
        if (err) return;
        if (!editingLocationId) {
            notifyError('Invalid location');
            return;
        }

        const payload = { location_id: editingLocationId, name: editLocationName.trim() };
        setSavingEditLocation(true);
        try {
            const response = await handleApiCall('put', endpoint.editLocation, payload);
            if (response?.data?.code === 200) {
                openMasterSuccess(response?.data?.message || 'Location updated successfully');
                closeEditLocation();
                await fetchLocationList();
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Failed to update location';
            notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setSavingEditLocation(false);
        }
    };

    const validateLocationName = (value) => {
        const v = (value || '').trim();
        if (!v) return 'MIPL location is required';
        if (v.length < 2) return 'MIPL location must be at least 2 characters';
        if (v.length > 100) return 'MIPL location is too long';
        if (!/^[A-Za-z0-9][A-Za-z0-9\s\-&,()./]*$/.test(v)) {
            return 'MIPL location contains invalid characters';
        }
        return '';
    };

    const handleAddLocationSubmit = async (e) => {
        e.preventDefault();
        const err = validateLocationName(locationName);
        setLocationError(err);
        if (err) return;

        const payload = { name: locationName.trim() };
        setSavingLocation(true);
        try {
            const response = await handleApiCall('post', endpoint.addLocation, payload);
            if (response?.data?.code === 200) {
                openMasterSuccess(response?.data?.message || 'Location added successfully');
                closeAddLocation();
                if (currentPage !== 1) setCurrentPage(1);
                else await fetchLocationList();
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Failed to add location';
            notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setSavingLocation(false);
        }
    };

    const handleLocationStatusToggle = async (locationId, currentStatus) => {
        const nextStatus = currentStatus === 1 ? 0 : 1;
        if (!locationId) return;
        setStatusUpdatingId(locationId);
        try {
            const response = await handleApiCall('put', endpoint.updateStatusLocation, {
                location_id: locationId,
                status: nextStatus,
            });
            if (response?.data?.code === 200) {
                setLocations((prev) =>
                    prev.map((loc) =>
                        loc.id === locationId
                            ? { ...loc, statusCode: nextStatus, status: nextStatus === 1 ? 'Active' : 'Inactive' }
                            : loc
                    )
                );
                notifySuccess(response?.data?.message || 'Location status updated');
                return;
            }
            const errMsg =
                response?.response?.data?.errorMessage ||
                response?.response?.data?.message ||
                response?.data?.message ||
                'Could not update location status';
            notifyError(errMsg);
        } catch {
            notifyError('Something went wrong. Please try again.');
        } finally {
            setStatusUpdatingId('');
        }
    };

    const displayFrom = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const displayTo = Math.min(currentPage * itemsPerPage, totalRecords);

    return (
        <>
        <Toastify/>
            <div className="tab-pane-content md:mt-7 mt-3">

                <div className='flex gap-x-2.5 md:mb-4 mb-3'>
                    
                    <input
                        type="text"
                        placeholder="Search..."
                        className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
                        value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                    />

                    <PermissionGate module={MODULES.MASTER} action="add">
                        <button className="btn btn-danger ml-auto" onClick={openAddLocation}>
                            + Add New
                        </button>
                    </PermissionGate>
                </div>

                {/* Table */}
                <div className="common-table-wrapper m-0">
                    <div className="table-responsive">
                        <table>
                            <thead>
                                <tr>
                                    <th>S.No.</th>
                                    <th>MIPL Location</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody className='max-[767px]:[&_td]:!py-2'>
                                {listLoading && (
                                    <tr>
                                        <td colSpan={4} className="text-center py-4 text-gray-500">
                                            Loading...
                                        </td>
                                    </tr>
                                )}
                                {!listLoading && locations.map((driver, index) => (
                                    <tr key={driver.id}>
                                        <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                        <td>{driver.location}</td>
                                        <td>{driver.status}</td>

                                        {/* Actions */}
                                        <td>
                                            <div className="flex items-center justify-center gap-2">
                                                <PermissionGate module={MODULES.MASTER} action="edit">
                                                    <label className="relative inline-flex items-center cursor-pointer w-9 min-w-9 h-5 bg-[#E11818] rounded-full my-0 [&:has(input:checked)]:bg-[#17B517] [&:has(input:checked)_.absolute]:!left-[17px] transition-all">
                                                        <input
                                                            type="checkbox"
                                                            className="hidden"
                                                            checked={driver.statusCode === 1}
                                                            onChange={() => handleLocationStatusToggle(driver.id, driver.statusCode)}
                                                            disabled={statusUpdatingId === driver.id || listLoading}
                                                        />
                                                        <div className="absolute left-[3px] top-0.5 w-4 h-4 bg-white rounded-full transition-all"></div>
                                                    </label>
                                                </PermissionGate>
                                                <PermissionGate module={MODULES.MASTER} action="edit">
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => openEditLocation(driver)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                openEditLocation(driver);
                                                            }
                                                        }}
                                                    >
                                                        <img src={EditIcon} className="h-7 min-w-[22px]" alt="" />
                                                    </span>
                                                </PermissionGate>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!listLoading && locations.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="text-center py-4 text-gray-500">
                                            No locations found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 📄 Pagination */}
                <div className="flex gap-x-4 items-center md:mt-4 mt-3">

                    <select
                        className='p-2 shadow-sm outline-none max-[767px]:text-xs'
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                    </select>
                    {/* Total Records */}
                    <div className="text-xs md:text-sm text-gray-500 ml-auto">
                        {listLoading
                            ? '—'
                            : totalRecords === 0
                                ? '0 records found'
                                : `${displayFrom}-${displayTo} of ${totalRecords} records found`}
                    </div>
                    {/* Pagination Controls */}
                    <div className="flex items-center gap-2 md:pr-8">
                        <button
                            type='button'
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={listLoading || currentPage === 1}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                        >
                            <img src={LeftAngle} className='invert grayscale brightness-100'/>
                        </button>
                        <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                        <span className="text-sm text-gray-600 text-nowrap">
                            of {totalPages}
                        </span>
                        <button
                            type='button'
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={listLoading || currentPage === totalPages || totalRecords === 0}
                            className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${currentPage===1 ? '' : 'cursor-pointer'}`}
                        >
                            <img src={RightAngle} className='invert grayscale brightness-100'/>
                        </button>

                    </div>
                </div>

            </div>

            {/* Add New Location Modal */}
            {addLocation && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] max-[767px]:px-4">
                    <div className="bg-white p-6 rounded-xl w-full max-w-96">
                        <h2 className="mb-4 text-center !text-xl font-UbuntuBold">Add MIPL Location</h2>
                        <form
                            className='font-UbuntuRegular [&_label]:mb-1.5 [&_label]:font-UbuntuMedium'
                            onSubmit={handleAddLocationSubmit}
                            noValidate
                        >
                            <div className='form-group mb-3'>
                                <label>MIPL Location</label>
                                <input
                                    className='form-control'
                                    value={locationName}
                                    onChange={(e) => {
                                        setLocationName(e.target.value);
                                        if (locationError) setLocationError('');
                                    }}
                                    onBlur={() => setLocationError(validateLocationName(locationName))}
                                    disabled={savingLocation}
                                />
                                {locationError && (
                                    <p className='text-danger small mt-1 mb-0'>{locationError}</p>
                                )}
                            </div>
                            <div className="flex justify-center gap-3 mt-3">
                                <button
                                    type='button'
                                    className="btn btn-primary flex-1 max-[767px]:!py-2.5"
                                    onClick={closeAddLocation}
                                    disabled={savingLocation}
                                >
                                    Cancel
                                </button>

                                <button
                                    type='submit'
                                    className="btn btn-danger flex-1 max-[767px]:!py-2.5"
                                    disabled={savingLocation}
                                >
                                    {savingLocation ? 'Adding...' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Location Modal */}
            {editLocation && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[9999] max-[767px]:px-4">
                    <div className="bg-white p-6 rounded-xl w-full max-w-96">
                        <h2 className="mb-4 text-center !text-xl font-UbuntuBold">Edit MIPL Location</h2>
                        <form
                            className="font-UbuntuRegular [&_label]:mb-1.5 [&_label]:font-UbuntuMedium"
                            onSubmit={handleEditLocationSubmit}
                            noValidate
                        >
                            <div className="form-group mb-3">
                                <label>MIPL Location</label>
                                <input
                                    className="form-control"
                                    value={editLocationName}
                                    onChange={(e) => {
                                        setEditLocationName(e.target.value);
                                        if (editLocationError) setEditLocationError('');
                                    }}
                                    onBlur={() => setEditLocationError(validateLocationName(editLocationName))}
                                    disabled={savingEditLocation}
                                />
                                {editLocationError && (
                                    <p className="text-danger small mt-1 mb-0">{editLocationError}</p>
                                )}
                            </div>
                            <div className="flex justify-center gap-3 mt-3">
                                <button
                                    type="button"
                                    className="btn btn-primary flex-1 max-[767px]:!py-2.5"
                                    onClick={closeEditLocation}
                                    disabled={savingEditLocation}
                                >
                                    Cancel
                                </button>

                                <button type="submit" className="btn btn-danger flex-1 max-[767px]:!py-2.5" disabled={savingEditLocation}>
                                    {savingEditLocation ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <MasterSuccessModal
                open={successModal.open}
                message={successModal.message}
                onClose={closeMasterSuccess}
            />
        </>
    );
};

export default LocationMaster;